/**
 * Purpose: Unified attachment storage abstraction layer
 *
 * How it works:
 * 1. Routes large files (≥5MB) to OPFS for better performance
 * 2. Falls back to IndexedDB when OPFS is not supported
 * 3. Keeps metadata and thumbnails in IndexedDB for fast querying
 * 4. Full content blobs stored in OPFS when possible
 * 5. Uses Web Worker for very large files (≥10MB) to avoid UI blocking
 * 6. Monitors quota and evicts old attachments when needed
 *
 * Key features:
 * - Transparent storage selection based on file size and browser support
 * - Consistent API regardless of underlying storage
 * - Memory-efficient handling of large video attachments
 * - Quota monitoring and automatic eviction
 * - Worker-based processing for very large files
 */

import { IS_OPFS_SUPPORTED } from "@/lib/environmentDetector";
import {
  writeToOPFS,
  readFromOPFS,
  deleteFromOPFS,
  clearOPFSAttachments,
} from "./opfsStorage";
import { checkQuotaForBlob, evictOldestAttachments } from "./storageManager";
import { isBlobReadable } from "./blobLiveness";
import { shouldUseWorker, writeWithWorker } from "./opfsWorkerClient";
import { normalizeMimeType } from "@/lib/mime";

/** Threshold for routing to OPFS (5MB) */
export const LARGE_FILE_THRESHOLD = 5 * 1024 * 1024;

/** Storage location indicator */
export type StorageLocation = "opfs" | "indexeddb";

/**
 * Determines which storage to use based on blob size and browser support
 *
 * @param blobSize - Size of the blob in bytes
 * @returns The storage location to use
 */
export function getStorageLocation(blobSize: number): StorageLocation {
  if (IS_OPFS_SUPPORTED && blobSize >= LARGE_FILE_THRESHOLD) {
    return "opfs";
  }
  return "indexeddb";
}

/**
 * Check if a blob should be stored in OPFS
 *
 * @param blob - The blob to check
 * @returns true if blob should use OPFS storage
 */
export function shouldUseOPFS(blob: Blob): boolean {
  return getStorageLocation(blob.size) === "opfs";
}

/**
 * Store attachment content with automatic storage selection
 *
 * Features:
 * - Checks quota before storing, evicts old attachments if needed
 * - Uses Web Worker for very large files (≥10MB)
 * - Falls back to main thread OPFS or IndexedDB as needed
 *
 * @param attachmentId - Unique identifier for the attachment
 * @param blob - The blob data to store
 * @returns Object with storage location and the blob (null if stored in OPFS)
 */
export async function storeAttachmentContent(
  attachmentId: string,
  blob: Blob,
): Promise<{
  storedIn: StorageLocation;
  blob: Blob | null;
}> {
  const location = getStorageLocation(blob.size);

  // For OPFS storage, check quota and potentially evict
  if (location === "opfs") {
    // Check if we have enough space
    const { canStore, needsEviction, bytesToFree } = await checkQuotaForBlob(
      blob.size,
    );

    // Evict old attachments if needed (never the one we're about to write).
    // Free what the quota gate actually demands (incl. the safety buffer) —
    // freeing only blob.size could never satisfy the gate on a full device.
    if (needsEviction && !canStore) {
      await evictOldestAttachments(bytesToFree, attachmentId);
    }

    // Try worker for very large files (non-blocking)
    if (shouldUseWorker(blob.size)) {
      const workerSuccess = await writeWithWorker(attachmentId, blob);
      if (workerSuccess) {
        return { storedIn: "opfs", blob: null };
      }
      // Fall through to main thread OPFS if worker fails
    }

    // Main thread OPFS write
    const success = await writeToOPFS(attachmentId, blob);
    if (success) {
      return { storedIn: "opfs", blob: null };
    }

    // Fallback to IndexedDB if OPFS write failed — but only with a blob whose
    // bytes are actually READABLE. The blob may be a lazy handle into a file
    // that no longer exists (e.g. the source of a forward, evicted mid-flight)
    // — which is often exactly why the OPFS write failed. Persisting it would
    // poison the cache: every reader sees a non-null blob, short-circuits, and
    // never falls back to the bucket. Storing NOTHING self-heals instead (a
    // null cache read re-downloads on demand).
    if (await isBlobReadable(blob)) {
      return { storedIn: "indexeddb", blob };
    }
    return { storedIn: "indexeddb", blob: null };
  }

  return { storedIn: "indexeddb", blob };
}

/**
 * Re-apply a MIME type to an OPFS-sourced blob.
 *
 * OPFS files carry no MIME metadata — `FileSystemFileHandle.getFile()` always
 * returns a blob with an empty `type`. Feeding that untyped blob to a `<video>`
 * element as an object URL makes iOS/Safari reject it with
 * `MEDIA_ERR_SRC_NOT_SUPPORTED` (the `[cache E4]` field report); desktop engines
 * content-sniff and play regardless. We restore the type from the entity's
 * stored `fileType` so the cached-playback path matches the streaming path.
 *
 * Empty-type only: a blob that already carries a type (e.g. from IndexedDB, or a
 * genuinely mistyped source) is left untouched — we fill a gap, never override.
 * `slice(0, size, type)` re-types without copying the underlying bytes.
 *
 * The type is run through `normalizeMimeType` (RFC-2045 validation) rather than a
 * loose `includes("/")` check: a stored `fileType` that is malformed but happens
 * to contain a slash (`"video/"`, `"x/y z"`, a leaked path) would otherwise be
 * stamped verbatim and make `<video>` reject the source — the exact failure this
 * helper exists to prevent. Anything not a valid MIME normalizes to `""` and is
 * left untyped, matching the pre-existing middleware behavior (attachments.ts).
 */
function withMimeType(blob: Blob | null, mimeType?: string): Blob | null {
  const validMime = mimeType ? normalizeMimeType(mimeType) : "";
  if (blob && !blob.type && validMime) {
    return blob.slice(0, blob.size, validMime);
  }
  return blob;
}

/**
 * Retrieve attachment content from storage
 *
 * @param attachmentId - Unique identifier for the attachment
 * @param indexedDBBlob - The blob stored in IndexedDB (if any)
 * @param storedIn - Where the content was stored
 * @param mimeType - The attachment's stored content type; re-applied to
 *   OPFS-sourced blobs, which come back with an empty `type` (see withMimeType)
 * @returns The blob content or null if not found
 */
export async function retrieveAttachmentContent(
  attachmentId: string,
  indexedDBBlob: Blob | undefined,
  storedIn?: StorageLocation,
  mimeType?: string,
): Promise<Blob | null> {
  // If we know it's stored in IndexedDB, return that
  if (storedIn === "indexeddb" && indexedDBBlob) {
    return indexedDBBlob;
  }

  // If we know it's in OPFS, try to read from there
  if (storedIn === "opfs") {
    return withMimeType(await readFromOPFS(attachmentId), mimeType);
  }

  // If storage location unknown, try IndexedDB first, then OPFS
  if (indexedDBBlob) {
    return indexedDBBlob;
  }

  // Try OPFS as fallback
  if (IS_OPFS_SUPPORTED) {
    return withMimeType(await readFromOPFS(attachmentId), mimeType);
  }

  return null;
}

/**
 * Delete attachment content from all storage locations
 *
 * @param attachmentId - Unique identifier for the attachment
 */
export async function deleteAttachmentContent(
  attachmentId: string,
): Promise<void> {
  // Always try to delete from OPFS (file might exist there)
  if (IS_OPFS_SUPPORTED) {
    await deleteFromOPFS(attachmentId);
  }
  // IndexedDB deletion is handled by the caller (Dexie)
}

/**
 * Clear all attachment content from OPFS
 * Used when clearing media/file attachments from storage settings
 */
export async function clearAllAttachmentContent(): Promise<void> {
  if (IS_OPFS_SUPPORTED) {
    await clearOPFSAttachments();
  }
}

export { IS_OPFS_SUPPORTED };
